import { DateRange } from "react-day-picker";
import { useQuery } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  DateFilterOption,
  HttpResponse,
  LeaderboardQueryParams,
  LeaderboardUser,
} from "@/types";

const prepareDateRangeQueryParams = (
  queryParams: URLSearchParams,
  dateRange?: DateRange,
) => {
  if (dateRange?.from) {
    queryParams.append("startDate", String(dateRange.from));
  }
  if (dateRange?.to) {
    // End date must be set to the following day to include the entire day
    // We handle this logic here so that the date range on the date range picker can be displayed correctly.
    // More info in dates.handleDateRangeFilter().
    const endDate = new Date(dateRange.to);
    endDate.setDate(endDate.getDate() + 1);
    queryParams.append("endDate", String(endDate));
  }
};

// Leaderboard data stays put for the life of the tab. Only the page's own
// observer (isRefetchedOnMount) refetches, so each visit gets fresh numbers
// without nested components triggering extra requests as they mount.
export function useGetLeaderboard(
  queryOptions: LeaderboardQueryParams,
  { isRefetchedOnMount = false }: { isRefetchedOnMount?: boolean } = {},
) {
  return useQuery({
    queryKey: ["leaderboard", queryOptions],
    staleTime: Infinity,
    refetchOnMount: isRefetchedOnMount ? "always" : true,
    queryFn: async () => {
      const { dateRange, timePeriod } = queryOptions;
      const queryParams = new URLSearchParams();

      if (timePeriod === DateFilterOption.Custom) {
        prepareDateRangeQueryParams(queryParams, dateRange);
      } else {
        queryParams.set("period", timePeriod);
      }

      const path = `staffer-metrics?${queryParams.toString()}`;
      const res = await guacHttpClient.get<
        LeaderboardUser[],
        HttpResponse<LeaderboardUser[]>
      >(path);

      return res.data;
    },
    placeholderData: (previousData) => previousData,
  });
}
